import {Injectable} from '@angular/core';
import {BehaviorSubject, firstValueFrom, Subject} from 'rxjs';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {MmProjectService} from '../../../mm-project/mm-project.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {ColumnState} from 'ag-grid-community';
import {LayoutStateModel} from '../../../mm-filter/layout/layout-state.model';
import {FieldDefaultNameEnum} from '@docma-mm-core/field/field-default-name.enum';
import {Layout} from '@docma-mm-core/layout/layout';
import {LayoutField} from '@docma-mm-core/layout/layout-field';
import {Field} from '@docma-mm-core/field/field';
import {FieldPermission} from '@docma-mm-core/field/field-permission.enum';
import {FieldPropertyEnum} from '@docma-mm-core/field/field-property.enum';
import {MmTranslateService} from '../../../mm-translate.service';

export enum SortCriterionEnum {
  Number = 'Number',
  Number2 = 'Number2',
  Description1 = 'Description1',
  Structure = 'Structure',
  CreatedOn = 'CreatedOn',
  List = 'List',
  Selected = 'Selected'
}

export enum SortDirectionEnum {
  Ascending = 'asc',
  Descending = 'desc'
}

export enum SortSourceEnum {
  Grid = 'grid',
  Tile = 'tile',
  Default = 'default',
  Layout = 'layout'
}

export interface TileSortState {
  criterion: SortCriterionEnum | null;
  direction: SortDirectionEnum | null;
  source: SortSourceEnum;
  timestamp?: number;
}

export interface SelectedAppliedOrder {
  timestamp: number;
  direction: SortDirectionEnum;
  orderedUids: Array<string | number>;
}

@Injectable()
export class MmFaultsSortingService {
  private sortStateSubject = new BehaviorSubject<TileSortState>({
    criterion: SortCriterionEnum.List,
    direction: SortDirectionEnum.Ascending,
    source: SortSourceEnum.Default
  });

  public sortState$ = this.sortStateSubject.asObservable();

  private currentLayoutId: number | null = null;
  private selectedSortActionSubject = new Subject<number>();
  public selectedSortAction$ = this.selectedSortActionSubject.asObservable();
  private selectedAppliedOrderSubject = new BehaviorSubject<SelectedAppliedOrder | null>(null);
  public selectedAppliedOrder$ = this.selectedAppliedOrderSubject.asObservable();

  constructor(
    private personalizationService: PersonalizationService,
    private mmProjectService: MmProjectService,
    private translateService: MmTranslateService
  ) {}

  async setSortCriterion(
    criterion: SortCriterionEnum,
    direction: SortDirectionEnum
  ): Promise<void> {
    const newState: TileSortState = {
      criterion,
      direction,
      source: SortSourceEnum.Tile,
      timestamp: Date.now()
    };

    this.sortStateSubject.next(newState);
    if (criterion === SortCriterionEnum.Selected) {
      this.selectedSortActionSubject.next(newState.timestamp);
    }
    await this.saveSortState(newState);
  }

  getCurrentSortState(): TileSortState {
    return this.sortStateSubject.value;
  }

  syncFromGridSort(columnStates: ColumnState[]): void {
    const sortedColumns = columnStates.filter(col => col.sort != null);

    if (sortedColumns.length === 0) {
      this.sortStateSubject.next({
        criterion: null,
        direction: null,
        source: SortSourceEnum.Grid
      });
      return;
    }

    if (sortedColumns.length > 1) {
      const allSameDirection = sortedColumns.every(
        col => col.sort === sortedColumns[0].sort
      );

      this.sortStateSubject.next({
        criterion: SortCriterionEnum.List,
        direction: allSameDirection
          ? (sortedColumns[0].sort as SortDirectionEnum)
          : null,
        source: SortSourceEnum.Grid
      });
      return;
    }

    const col = sortedColumns[0];
    const criterion = this.mapGridColumnToCriterion(col.colId);

    this.sortStateSubject.next({
      criterion: criterion || SortCriterionEnum.List,
      direction: col.sort as SortDirectionEnum,
      source: SortSourceEnum.Grid
    });
  }

  getGridColumnState(): ColumnState[] {
    const state = this.sortStateSubject.value;

    if (state.criterion === SortCriterionEnum.List ||
        state.criterion === SortCriterionEnum.Structure ||
        state.criterion === SortCriterionEnum.Selected) {
      return [];
    }

    const columnId = this.mapCriterionToGridColumn(state.criterion);
    if (!columnId) return [];

    return [{
      colId: columnId,
      sort: state.direction,
      sortIndex: 0
    }];
  }

  resetToDefault(): void {
    this.sortStateSubject.next({
      criterion: SortCriterionEnum.List,
      direction: SortDirectionEnum.Ascending,
      source: SortSourceEnum.Default
    });
  }

  async loadSortState(projectId: string): Promise<void> {
    console.log('[MmFaultsSortingService] Loading sort state for project:', projectId);

    const savedState = await this.personalizationService.getProjectValue(
      projectId,
      PersonalizationKeysEnum.FaultsTileSort
    );

    if (savedState) {
      console.log('[MmFaultsSortingService] Loaded saved sort state:', savedState);
      this.sortStateSubject.next({
        ...savedState,
        source: SortSourceEnum.Default
      });
    } else {
      const currentState = this.sortStateSubject.value;
      if (currentState.source === SortSourceEnum.Layout) {
        return;
      }
      console.log('[MmFaultsSortingService] No saved sort state found, using default (List)');
      this.resetToDefault();
    }
  }

  async saveSortState(state: TileSortState): Promise<void> {
    const currentProject = await firstValueFrom(
      this.mmProjectService.currentProject
    );

    if (currentProject) {
      console.log('[MmFaultsSortingService] Saving sort state for project:', currentProject.MyDocmaUid, state);
      await this.personalizationService.setProjectValue(
        currentProject.MyDocmaUid,
        PersonalizationKeysEnum.FaultsTileSort,
        state
      );
      console.log('[MmFaultsSortingService] Sort state saved successfully');
    } else {
      console.warn('[MmFaultsSortingService] Cannot save sort state: no current project');
    }
  }

  async clearSavedSortState(): Promise<void> {
    const currentProject = await firstValueFrom(
      this.mmProjectService.currentProject
    );

    if (currentProject) {
      await this.personalizationService.setProjectValue(
        currentProject.MyDocmaUid,
        PersonalizationKeysEnum.FaultsTileSort,
        null
      );
    }
  }

  getAvailableCriteria(layoutState: LayoutStateModel, fieldPermissionsByProp: { [prop: string]: FieldPermission }): SortCriterionEnum[] {
    const criteria: SortCriterionEnum[] = [];

    const roleFieldMappings = [
      { criterion: SortCriterionEnum.Number, prop: FieldPropertyEnum.Number1, defaultName: FieldDefaultNameEnum.Number },
      { criterion: SortCriterionEnum.Number2, prop: FieldPropertyEnum.Number2, defaultName: FieldDefaultNameEnum.Number2 },
      { criterion: SortCriterionEnum.Description1, prop: FieldPropertyEnum.Desc1_1, defaultName: FieldDefaultNameEnum.Description1 }
    ];

    roleFieldMappings.forEach(({ criterion, prop, defaultName }) => {
      const permission = fieldPermissionsByProp?.[prop];
      if (permission != null) {
        if (permission !== FieldPermission.None) {
          criteria.push(criterion);
        }
      } else {
        const field = layoutState?.layout?.Fields?.find(
          f => f.Field?.DefaultName === defaultName
        );
        if (field) {
          criteria.push(criterion);
        }
      }
    });

    criteria.push(
      SortCriterionEnum.Structure,
      SortCriterionEnum.CreatedOn,
      SortCriterionEnum.List,
      SortCriterionEnum.Selected
    );

    return criteria;
  }

  getCriterionLabel(criterion: SortCriterionEnum, layoutState: LayoutStateModel): string {
    const fieldMapping = {
      [SortCriterionEnum.Number]: FieldDefaultNameEnum.Number,
      [SortCriterionEnum.Number2]: FieldDefaultNameEnum.Number2,
      [SortCriterionEnum.Description1]: FieldDefaultNameEnum.Description1
    };

    const defaultName = fieldMapping[criterion];
    if (defaultName) {
      const field = layoutState?.layout?.Fields?.find(
        f => f.Field?.DefaultName === defaultName
      );
      if (field?.Field?.GlobalName) {
        return field.Field.GlobalName;
      }
    }

    const translationKeyMap = {
      [SortCriterionEnum.Number]: 'MM.field.defectNo',
      [SortCriterionEnum.Number2]: 'MM.field.agNr',
      [SortCriterionEnum.Description1]: 'MM.field.description1',
      [SortCriterionEnum.Structure]: 'MM.fault.structure',
      [SortCriterionEnum.CreatedOn]: 'MM.fault.createdOn',
      [SortCriterionEnum.List]: 'MM.fault.list',
      [SortCriterionEnum.Selected]: 'MM.fault.selected'
    };

    const translationKey = translationKeyMap[criterion];
    if (translationKey) {
      return this.translateService.instant(translationKey);
    }

    return criterion;
  }

  refreshSelectedSort(): void {
    const current = this.sortStateSubject.value;
    if (current.criterion === SortCriterionEnum.Selected) {
      const ts = Date.now();
      this.sortStateSubject.next({
        ...current,
        timestamp: ts
      });
      this.selectedSortActionSubject.next(ts);
    }
  }

  setSelectedAppliedOrder(
    orderedUids: Array<string | number>,
    direction: SortDirectionEnum,
    timestamp?: number
  ): void {
    const ts = timestamp ?? Date.now();
    this.selectedAppliedOrderSubject.next({
      timestamp: ts,
      direction,
      orderedUids
    });
  }

  getSelectedAppliedOrder(): SelectedAppliedOrder | null {
    return this.selectedAppliedOrderSubject.value;
  }


  updateLayoutStateWithTileSort(
    criterion: SortCriterionEnum,
    direction: SortDirectionEnum,
    currentLayoutState: LayoutStateModel
  ): Layout {
    console.log('[MmFaultsSortingService] Updating layout state for tile sort:', criterion, direction);

    const userState = !currentLayoutState?.userState
      ? new Layout()
      : JSON.parse(JSON.stringify(currentLayoutState.userState));

    if (!userState.Fields) {
      userState.Fields = [];
    }

    const fieldMapping = {
      [SortCriterionEnum.Number]: FieldDefaultNameEnum.Number,
      [SortCriterionEnum.Number2]: FieldDefaultNameEnum.Number2,
      [SortCriterionEnum.Description1]: FieldDefaultNameEnum.Description1,
      [SortCriterionEnum.CreatedOn]: FieldDefaultNameEnum.CreatedOn,
      [SortCriterionEnum.Structure]: FieldDefaultNameEnum.Structure
    };

    const targetFieldName = fieldMapping[criterion];

    userState.Fields.forEach(f => {
      f.Sorting = 0;
      f.IsDescending = false;
    });

    delete userState._fieldSorting;
    delete userState._structureSorting;

    if (criterion === SortCriterionEnum.List || criterion === SortCriterionEnum.Selected) {
      console.log('[MmFaultsSortingService] List or Selected criterion - clearing all sorts');

      const layoutFields = currentLayoutState?.layout?.Fields ?? [];
      for (const lf of layoutFields) {
        if (lf.Sorting && lf.Sorting !== 0) {
          let userField = userState.Fields.find(uf => uf.Field?.Id === lf.Field?.Id);
          if (!userField) {
            userField = new LayoutField({
              Field: new Field({ Id: lf.Field.Id }),
              Sorting: 0,
              IsDescending: false
            });
            userState.Fields.push(userField);
          }
        }
      }

      return userState;
    }

    console.log('[MmFaultsSortingService] Looking for field:', targetFieldName);
    console.log('[MmFaultsSortingService] Available fields in layout:', currentLayoutState?.layout?.Fields?.map(f => f.Field?.DefaultName));

    const layoutField = currentLayoutState?.layout?.Fields?.find(
      f => f.Field?.DefaultName === targetFieldName
    );

    if (!layoutField) {
      console.warn('[MmFaultsSortingService] Field not found in layout:', targetFieldName);
      console.warn('[MmFaultsSortingService] criterion:', criterion);

      if (criterion === SortCriterionEnum.Structure) {
        console.log('[MmFaultsSortingService] Structure is a special case - will be handled in setSorting');
        userState._structureSorting = {
          criterion: criterion,
          direction: direction
        };
        return userState;
      }

      if (targetFieldName) {
        userState._fieldSorting = {
          defaultName: targetFieldName,
          direction: direction
        };
      }

      return userState;
    }

    let userField = userState.Fields.find(uf => uf.Field?.Id === layoutField.Field.Id);

    console.log('[updateLayoutStateWithTileSort] direction param:', direction);
    console.log('[updateLayoutStateWithTileSort] SortDirectionEnum.Descending:', SortDirectionEnum.Descending);
    console.log('[updateLayoutStateWithTileSort] Are they equal?:', direction === SortDirectionEnum.Descending);

    if (!userField) {
      userField = new LayoutField({
        Field: new Field({ Id: layoutField.Field.Id }),
        Sorting: 1,
        IsDescending: direction === SortDirectionEnum.Descending
      });
      console.log('[updateLayoutStateWithTileSort] Created new field, IsDescending:', userField.IsDescending);
      userState.Fields.push(userField);
    } else {
      userField.Sorting = 1;
      userField.IsDescending = direction === SortDirectionEnum.Descending;
      console.log('[updateLayoutStateWithTileSort] Updated existing field, IsDescending:', userField.IsDescending);
    }

    console.log('[MmFaultsSortingService] Layout state updated successfully');
    return userState;
  }

  private mapCriterionToGridColumn(criterion: SortCriterionEnum): string | null {
    const mapping = {
      [SortCriterionEnum.Number]: FieldDefaultNameEnum.Number,
      [SortCriterionEnum.Number2]: FieldDefaultNameEnum.Number2,
      [SortCriterionEnum.Description1]: FieldDefaultNameEnum.Description1,
      [SortCriterionEnum.CreatedOn]: FieldDefaultNameEnum.CreatedOn,
      [SortCriterionEnum.Structure]: null,
      [SortCriterionEnum.Selected]: null,
      [SortCriterionEnum.List]: null
    };
    return mapping[criterion];
  }

  private mapGridColumnToCriterion(columnId: string): SortCriterionEnum | null {
    const mapping = {
      [FieldDefaultNameEnum.Number]: SortCriterionEnum.Number,
      [FieldDefaultNameEnum.Number2]: SortCriterionEnum.Number2,
      [FieldDefaultNameEnum.Description1]: SortCriterionEnum.Description1,
      [FieldDefaultNameEnum.CreatedOn]: SortCriterionEnum.CreatedOn
    };
    return mapping[columnId] || null;
  }

  async syncFromLayoutState(layoutState: LayoutStateModel): Promise<void> {
    if (!layoutState?.layout?.Fields) {
      return;
    }

    const currentState = this.sortStateSubject.value;
    const newLayoutId = layoutState.layout?.Id;
    const layoutChanged = this.currentLayoutId !== newLayoutId;

    const isInitialLoad = this.currentLayoutId === null;

    if (layoutChanged) {
      this.currentLayoutId = newLayoutId;
    }

    if (isInitialLoad && currentState.source === SortSourceEnum.Default && currentState.criterion !== SortCriterionEnum.List) {
      return;
    }

    if (!layoutChanged && (currentState.source === SortSourceEnum.Tile || currentState.source === SortSourceEnum.Layout || currentState.source === SortSourceEnum.Default || currentState.source === SortSourceEnum.Grid)) {
      return;
    }

    const userStateFields = layoutState?.userState?.Fields ?? [];
    const layoutFields = layoutState.layout.Fields;

    const sortedFields: { defaultName: string; sorting: number; isDescending: boolean }[] = [];

    for (const lf of layoutFields) {
      let sorting = 0;
      let isDescending = false;

      if (layoutChanged) {
        if (lf.Sorting && lf.Sorting !== 0) {
          sorting = lf.Sorting;
          isDescending = lf.IsDescending ?? false;
        }
      } else {
        const userField = userStateFields.find(uf => uf.Field?.Id === lf.Field?.Id);

        if (userField && userField.Sorting != null && userField.IsDescending != null) {
          sorting = userField.Sorting;
          isDescending = userField.IsDescending;
        } else if (lf.Sorting && lf.Sorting !== 0) {
          sorting = lf.Sorting;
          isDescending = lf.IsDescending ?? false;
        }
      }

      if (sorting !== 0 && lf.Field?.DefaultName) {
        sortedFields.push({
          defaultName: lf.Field.DefaultName,
          sorting,
          isDescending
        });
      }
    }

    sortedFields.sort((a, b) => a.sorting - b.sorting);

    const hasLayoutSorting = sortedFields.length > 0;

    if (layoutChanged && hasLayoutSorting && !isInitialLoad) {
      await this.clearSavedSortState();
    }

    if (sortedFields.length === 0) {
      this.sortStateSubject.next({
        criterion: null,
        direction: null,
        source: SortSourceEnum.Layout
      });
      return;
    }

    if (sortedFields.length > 1) {
      const allSameDirection = sortedFields.every(f => f.isDescending === sortedFields[0].isDescending);

      this.sortStateSubject.next({
        criterion: SortCriterionEnum.List,
        direction: allSameDirection
          ? (sortedFields[0].isDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending)
          : null,
        source: SortSourceEnum.Layout
      });
      return;
    }

    const field = sortedFields[0];
    const criterion = this.mapGridColumnToCriterion(field.defaultName);

    this.sortStateSubject.next({
      criterion: criterion || SortCriterionEnum.List,
      direction: field.isDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending,
      source: SortSourceEnum.Layout
    });

  }

  getLayoutDefaultSorting(layoutState: LayoutStateModel): { sortedFields: { defaultName: string; sorting: number; isDescending: boolean; fieldId: number }[]; criterion: SortCriterionEnum; direction: SortDirectionEnum } {
    if (!layoutState?.layout?.Fields) {
      return { sortedFields: [], criterion: SortCriterionEnum.List, direction: SortDirectionEnum.Ascending };
    }

    const layoutFields = layoutState.layout.Fields;
    const sortedFields: { defaultName: string; sorting: number; isDescending: boolean; fieldId: number }[] = [];

    for (const lf of layoutFields) {
      if (lf.Sorting && lf.Sorting !== 0 && lf.Field?.DefaultName) {
        sortedFields.push({
          defaultName: lf.Field.DefaultName,
          sorting: lf.Sorting,
          isDescending: lf.IsDescending ?? false,
          fieldId: lf.Field.Id
        });
      }
    }

    sortedFields.sort((a, b) => a.sorting - b.sorting);

    if (sortedFields.length === 0) {
      return { sortedFields: [], criterion: SortCriterionEnum.List, direction: SortDirectionEnum.Ascending };
    }

    if (sortedFields.length > 1) {
      const allSameDirection = sortedFields.every(f => f.isDescending === sortedFields[0].isDescending);
      return {
        sortedFields,
        criterion: SortCriterionEnum.List,
        direction: allSameDirection
          ? (sortedFields[0].isDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending)
          : SortDirectionEnum.Ascending
      };
    }

    const field = sortedFields[0];
    const criterion = this.mapGridColumnToCriterion(field.defaultName);

    return {
      sortedFields,
      criterion: criterion || SortCriterionEnum.List,
      direction: field.isDescending ? SortDirectionEnum.Descending : SortDirectionEnum.Ascending
    };
  }

  async applyLayoutSorting(layoutState: LayoutStateModel): Promise<void> {
    const { criterion, direction } = this.getLayoutDefaultSorting(layoutState);

    const newState: TileSortState = {
      criterion,
      direction,
      source: SortSourceEnum.Tile,
      timestamp: Date.now()
    };

    this.sortStateSubject.next(newState);
    await this.saveSortState(newState);
  }
}
